import React, { useState, useEffect } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

const Login = () => {
  const { loginWithGoogle, loginWithEmail, user, switchEnvironment, environment } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const environments = [
    { name: 'Development', color: 'bg-green-500' },
    { name: 'QA', color: 'bg-yellow-500' },
    { name: 'Production', color: 'bg-red-500' }
  ];

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const getEnvColor = (envName: string) => {
    const env = environments.find(e => e.name === envName);
    return env ? env.color : 'bg-gray-200';
  };

  const currentEnvColor = getEnvColor(environment);

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      toast.success('Logged in successfully!');
      navigate('/');
    } catch (error: any) {
      toast.error('Failed to login with Google: ' + error.message);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    try {
      await loginWithEmail(email, password);
      toast.success('Logged in successfully!');
      navigate('/');
    } catch (error: any) {
      toast.error('Failed to login with Email: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-50">
      <Toaster />
      {/* Environment Selector */}
      <div className="w-full flex justify-center py-4 bg-white shadow-sm mb-8">
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
          {environments.map((env) => (
            <button
              key={env.name}
              onClick={() => switchEnvironment(env.name)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${environment === env.name
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${env.color}`}></span>
                <span>{env.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Top Highlight Bar */}
        <div className={`h-2 w-full ${currentEnvColor}`}></div>

        <div className="p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Welcome Back</h2>
          <p className="text-center text-gray-500 mb-8">Login to <span className="font-semibold">{environment}</span></p>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center space-x-2 border border-gray-300 rounded-lg p-3 hover:bg-gray-50 transition-colors mb-6"
          >
            <FcGoogle size={24} />
            <span className="text-gray-700 font-medium">Login with Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or login with email</span>
            </div>
          </div>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleEmailLogin}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-medium py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              Sign In
            </button>
          </form>

          {/* Email Link Option */}
          <div className="mt-6 text-center">
            <button className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline">
              Receive access link by email
            </button>
          </div>
        </div>

        {/* Footer info */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">Protected by reCAPTCHA and subject to the Privacy Policy.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
